/**
 * A listener on this bridge. Payloads arrive as JSON off the hub's status
 * channel, so a handler is handed `unknown` and narrows what it needs — the
 * user of the bridge says what an event carries (card t_016c7a0e).
 */
type StatusEventHandler = (...args: unknown[]) => void;

interface StatusBridgeMessage {
  type?: string;
  event?: string;
  data?: unknown;
  [key: string]: unknown;
}

class WebRTCStatusBridge {
  connected = false;

  private listeners = new Map<string, Set<StatusEventHandler>>();
  private statusChannel: RTCDataChannel | null = null;

  attachChannel(channel: RTCDataChannel): void {
    if (this.statusChannel === channel) {
      return;
    }

    if (this.statusChannel) {
      this.statusChannel.onmessage = null;
      this.statusChannel.onopen = null;
      this.statusChannel.onclose = null;
      this.statusChannel.onerror = null;
    }

    this.statusChannel = channel;
    this.connected = channel.readyState === 'open';

    channel.onopen = () => {
      this.connected = true;
      this.dispatch('connect');
    };

    channel.onclose = () => {
      this.connected = false;
      this.dispatch('disconnect');
    };

    channel.onerror = (error) => {
      this.dispatch('error', error);
    };

    channel.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    if (this.connected) {
      queueMicrotask(() => this.dispatch('connect'));
    }
  }

  clearChannel(channel?: RTCDataChannel): void {
    if (channel && this.statusChannel !== channel) {
      return;
    }

    if (this.statusChannel) {
      this.statusChannel.onmessage = null;
      this.statusChannel.onopen = null;
      this.statusChannel.onclose = null;
      this.statusChannel.onerror = null;
    }

    this.statusChannel = null;
    if (this.connected) {
      this.connected = false;
      this.dispatch('disconnect');
    }
  }

  on(event: string, callback: StatusEventHandler): this {
    const handlers = this.listeners.get(event) || new Set<StatusEventHandler>();
    handlers.add(callback);
    this.listeners.set(event, handlers);
    return this;
  }

  off(event: string, callback?: StatusEventHandler): this {
    if (!callback) {
      this.listeners.delete(event);
      return this;
    }

    const handlers = this.listeners.get(event);
    if (!handlers) {
      return this;
    }

    handlers.delete(callback);
    if (handlers.size === 0) {
      this.listeners.delete(event);
    }
    return this;
  }

  /**
   * socket.io-style emit: sends an event to the hub over the status data
   * channel. This is what `socketStore.socket?.emit(...)` resolves to while the
   * socket store is bridged to the WebRTC session in cloud mode.
   */
  emit(event: string, ...args: unknown[]): boolean {
    if (!this.statusChannel || this.statusChannel.readyState !== 'open') {
      console.warn('[WebRTCStatusBridge] Status channel not open; dropping emit:', event);
      return false;
    }
    try {
      const data = args.length === 0 ? null : args.length === 1 ? args[0] : args;
      this.statusChannel.send(JSON.stringify({ type: 'event', event, data }));
      return true;
    } catch (error) {
      console.error('[WebRTCStatusBridge] Failed to send event:', event, error);
      return false;
    }
  }

  /** Dispatch an event to locally-registered listeners (incoming hub events + lifecycle). */
  private dispatch(event: string, ...args: unknown[]): boolean {
    const handlers = this.listeners.get(event);
    if (!handlers || handlers.size === 0) {
      return false;
    }

    handlers.forEach((handler) => {
      try {
        handler(...args);
      } catch (error) {
        console.error('[WebRTCStatusBridge] Listener failed for event:', event, error);
      }
    });
    return true;
  }

  private handleMessage(data: string | ArrayBuffer | Blob): void {
    if (typeof data !== 'string') {
      return;
    }

    try {
      const message = JSON.parse(data) as StatusBridgeMessage;
      if (message.type !== 'event' || !message.event) {
        return;
      }

      this.dispatch(message.event, message.data);
    } catch (error) {
      console.error('[WebRTCStatusBridge] Failed to parse status message:', error);
    }
  }
}

export const webRTCStatusBridge = new WebRTCStatusBridge();
export default webRTCStatusBridge;
